import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useChatUiStore } from '@/stores/chatUiStore';
import { cn } from '@/lib/utils';
import { useAiCreditsStatus } from '@/hooks/queries/useAiCredits';
import { ChatPanel } from './ChatPanel';
import { AiCreditsIndicator } from './AiCreditsIndicator';
import { useUnreadInsightsCount } from './ChatInsightAlerts';
import { useFloatingDockOffset } from './useFloatingDockOffset';
import { VoiceAssistantOverlay } from '@/components/voice/VoiceAssistantOverlay';
import type { VoiceContext } from '@/components/voice/voiceContextPrompts';

const LONG_PRESS_MS = 500;

function usePageVoiceContext(): VoiceContext {
  const { pathname } = useLocation();
  return useMemo<VoiceContext>(() => {
    if (pathname.includes('/products')) return { type: 'create-product' };
    if (pathname.includes('/categories')) return { type: 'create-categories' };
    if (pathname.includes('/dashboard')) return { type: 'sales-summary' };
    if (pathname.includes('/stock') || pathname.includes('/inventory'))
      return { type: 'stock-check' };
    return { type: 'general' };
  }, [pathname]);
}

function useIsMacLike(): boolean {
  return useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');
  }, []);
}

export function ChatFab() {
  const { t } = useTranslation('dashboard');
  const chatOpen = useChatUiStore((s) => s.open);
  const setChatOpen = useChatUiStore((s) => s.setOpen);
  const toggleChat = useChatUiStore((s) => s.toggle);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const didLongPressRef = useRef(false);
  const voiceContext = usePageVoiceContext();
  const { data: creditsStatus } = useAiCreditsStatus();
  const unreadCount = useUnreadInsightsCount();
  const bottomOffset = useFloatingDockOffset();
  const isMac = useIsMacLike();
  const isExhausted =
    creditsStatus && !creditsStatus.unlimited
      ? creditsStatus.balance !== undefined
        ? creditsStatus.balance <= 0
        : !creditsStatus.available
      : false;
  const canPortal = typeof document !== 'undefined';

  const handlePointerDown = useCallback(() => {
    didLongPressRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      didLongPressRef.current = true;
      navigator.vibrate?.(50);
      setVoiceOpen(true);
    }, LONG_PRESS_MS);
  }, []);

  const handlePointerUp = useCallback(() => {
    clearTimeout(longPressTimerRef.current);
    if (!didLongPressRef.current) {
      setChatOpen(true);
    }
  }, []);

  const handlePointerLeave = useCallback(() => {
    clearTimeout(longPressTimerRef.current);
  }, []);

  // Cmd+J / Ctrl+J → ouvrir l'assistant (cohabite avec Cmd+K du CommandPalette)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key.toLowerCase() !== 'j') return;
      e.preventDefault();
      toggleChat();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleChat]);

  const shortcutSymbol = isMac ? '⌘' : 'Ctrl';
  const ariaLabel = `${t('chat_fab_label', { defaultValue: 'Ask AI' })} (${shortcutSymbol}J) — ${t(
    'chat_fab_hint_voice',
    { defaultValue: 'Hold for voice input' },
  )}`;

  const bubble = (
    <div
      className={cn(
        // Desktop : le déclencheur IA vit dans la pilule haut-droite
        // (FloatingTopActions) → on masque le FAB bas-droite sur lg+.
        'fixed right-4 z-[40] flex flex-col items-end gap-2 lg:hidden',
        'pointer-events-auto',
      )}
      style={{ bottom: `${bottomOffset}px` }}
    >
      {creditsStatus ? (
        <div className="pointer-events-auto hidden lg:block">
          <AiCreditsIndicator compact />
        </div>
      ) : null}

      <button
        type="button"
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerLeave}
        aria-label={ariaLabel}
        title={t('chat_fab_hint_voice', { defaultValue: 'Hold for voice input' })}
        data-testid="chat-fab"
        data-tour="chat-fab"
        className={cn(
          'group relative inline-flex items-center justify-center rounded-full',
          // Mobile: 56px solid dark circle (HeyGen-style)
          'h-14 w-14 bg-charcoal-950 text-white border border-charcoal-900',
          // Desktop+ : pill with label + shortcut
          'lg:h-11 lg:w-auto lg:gap-2.5 lg:pl-3.5 lg:pr-2 lg:bg-white lg:text-ink-primary lg:border-slate-200',
          'lg:dark:bg-charcoal-900 lg:dark:text-charcoal-100 lg:dark:border-charcoal-700',
          'shadow-[0_8px_24px_-8px_rgba(15,23,42,0.28)] lg:shadow-[0_8px_24px_-8px_rgba(15,23,42,0.16)]',
          'transition-[transform,box-shadow] duration-200 ease-out',
          'hover:-translate-y-px hover:shadow-[0_12px_32px_-8px_rgba(15,23,42,0.32)] lg:hover:shadow-[0_12px_32px_-8px_rgba(15,23,42,0.22)]',
          'active:scale-[0.98] active:duration-75',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-500 focus-visible:ring-offset-2',
          'select-none touch-none',
          'motion-reduce:transition-none motion-reduce:hover:translate-y-0',
          isExhausted && 'lg:border-rose-200 lg:dark:border-rose-500/40',
        )}
      >
        <span
          className={cn(
            'relative grid size-7 place-items-center rounded-full lg:size-6',
            // Mobile: lime accent on dark fab
            'bg-lime-400 text-charcoal-950',
            // Desktop: subtle lime tint
            'lg:bg-lime-50 lg:text-lime-600 lg:dark:bg-lime-500/15 lg:dark:text-lime-300',
            isExhausted &&
              'bg-rose-400 text-charcoal-950 lg:bg-rose-50 lg:text-rose-600 lg:dark:bg-rose-500/15 lg:dark:text-rose-300',
          )}
          aria-hidden="true"
        >
          <Sparkles className="size-4 lg:size-3.5" strokeWidth={2.25} />
          {unreadCount > 0 ? (
            <span
              className={cn(
                'absolute -right-0.5 -top-0.5 grid size-4 place-items-center rounded-full',
                'border border-white bg-rose-500 font-mono text-[9px] font-bold leading-none text-white',
                'dark:border-charcoal-900',
              )}
              aria-hidden="true"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          ) : null}
        </span>

        <span className="hidden text-sm font-medium leading-none lg:inline">
          {t('chat_fab_label', { defaultValue: 'Ask AI' })}
        </span>

        <span
          className={cn(
            'ml-0.5 hidden h-7 items-center gap-0.5 rounded-full px-2 lg:inline-flex',
            'bg-warm-100 text-ink-soft dark:bg-charcoal-800 dark:text-charcoal-300',
            'font-mono text-[11px] font-medium leading-none',
          )}
          aria-hidden="true"
        >
          <span>{shortcutSymbol}</span>
          <span>J</span>
        </span>
      </button>
    </div>
  );

  return (
    <>
      {!chatOpen && !voiceOpen && canPortal
        ? createPortal(bubble, document.body)
        : !chatOpen && !voiceOpen
          ? bubble
          : null}
      <ChatPanel open={chatOpen} onOpenChange={setChatOpen} />
      <VoiceAssistantOverlay open={voiceOpen} onOpenChange={setVoiceOpen} context={voiceContext} />
    </>
  );
}
